'use client'
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { usePathname } from 'next/navigation'

import { prefetchImages } from '@/components/image/image-prefetch'
import { useClientContext } from '@/hooks/client/client-provider'
import { isDarkNavPageType, isDarkNavPath } from '@/lib/nav/dark-nav-routes'
import type { NavConfig, NavContainerConfig, NavPanelConfig, NavTabConfig } from '@/types/nav-types'

import { NavContext } from './nav-context'
import { NavFamily, NavSelection } from './nav-types'
import useClickaway from './use-click-away'
import { useIsNavMobileView } from './use-is-nav-mobile'
import { useScrollAdjust } from './use-scroll-adjust'

// Cache-buster for /api/nav, which is served with a long stale-while-revalidate.
// Bump whenever the NavConfig payload shape changes, so a browser holding a
// freshly-deployed shell can never pair it with a CDN copy of the old shape.
const NAV_PAYLOAD_VERSION = '22'

interface ProviderProps {
  nav: NavConfig
  navLoader?: {
    locale: string
    version: 'draft' | 'published'
    renderMode: 'public' | 'preview' | 'dev'
  }
  children: ReactNode
}

// Nav interaction should come through this. The disclaimer bar's presence is
// decided server-side — ServerNav renders it in normal flow above the nav —
// so nothing here drives it, and its (variable) height is handled
// structurally: no context flag, no CSS :has(), no hardcoded offsets.
export const NavContextProvider: React.FC<ProviderProps> = ({ nav, navLoader, children }) => {
  const { pageType } = useClientContext()
  const isMobile = useIsNavMobileView()
  const pathname = usePathname()
  const isDarkNav = isDarkNavPath(pathname) || isDarkNavPageType(pageType)
  const [resolvedNav, setResolvedNav] = useState(nav)
  const [current, setCurrent] = useState<NavSelection>({})
  const [isMenuVisible, setMenuVisible] = useState(true)
  const [isMobileMenuVisible, setMobileMenuVisible] = useState(false)
  const [isHomepageMenuTextWhite, setMenuTextWhite] = useState(pageType === 'home')
  const [firstOpen, setFirstOpen] = useState(false)
  const [containersReady, setContainersReady] = useState(false)
  const [debugNav, setDebugNav] = useState(false)
  const [navLoadFailed, setNavLoadFailed] = useState(false)
  const [navLoading, setNavLoading] = useState(false)

  const navRef = useRef<HTMLElement | null>(null)
  const registerNavRef = useCallback((el: HTMLElement | null) => {
    navRef.current = el
  }, [])

  useEffect(() => {
    setResolvedNav(nav)
    setContainersReady(false)
    setNavLoadFailed(false)
    setNavLoading(false)
  }, [nav])

  useEffect(() => {
    if (firstOpen) {
      prefetchImages('img[data-load-time="nav"]')
    }
  }, [firstOpen])

  // The mobile sheet's front screen shows the featured picks before any
  // container is selected, so opening it must fetch the deferred nav (which
  // otherwise waits for a container) and start the nav image prefetch.
  useEffect(() => {
    if (isMobileMenuVisible) {
      setFirstOpen(true)
      setContainersReady(true)
    }
  }, [isMobileMenuVisible])

  useEffect(() => {
    setDebugNav(new URLSearchParams(window.location.search).get('debug-nav') === 'true')
  }, [resolvedNav.deferred])

  const loadFullNav = useCallback(
    async (force = false) => {
      if (!resolvedNav.deferred || navLoading || !navLoader) return
      // Keep the failed guard for the auto-effect (avoids an infinite retry
      // loop), but let retryNav bypass it with `force`.
      if (navLoadFailed && !force) return

      setNavLoadFailed(false)
      setNavLoading(true)
      try {
        const params = new URLSearchParams({
          locale: navLoader.locale,
          version: navLoader.version,
          renderMode: navLoader.renderMode,
          v: NAV_PAYLOAD_VERSION,
        })
        const response = await fetch(`/api/nav?${params.toString()}`, { credentials: 'same-origin' })
        if (!response.ok) {
          throw new Error(`Nav request failed with ${response.status}`)
        }
        const nextNav = (await response.json()) as NavConfig
        setResolvedNav(nextNav)
      } catch (error) {
        setNavLoadFailed(true)
        console.error('[nav] failed to load deferred nav', error)
      } finally {
        setNavLoading(false)
      }
    },
    [navLoadFailed, navLoader, navLoading, resolvedNav.deferred]
  )

  useEffect(() => {
    if (containersReady && resolvedNav.deferred) {
      void loadFullNav()
    }
  }, [containersReady, loadFullNav, resolvedNav.deferred])

  const retryNav = useCallback(() => {
    void loadFullNav(true)
  }, [loadFullNav])

  // The deferred shell is showing (full nav not yet fetched) and we haven't
  // given up — tabs/panels render skeletons while this is true.
  const isContentLoading = !!resolvedNav.deferred && !navLoadFailed

  // Defer container rendering until browser is idle when the full nav is
  // already present. Deferred nav waits for first interaction to avoid
  // adding the dropdown payload to the initial load.
  useEffect(() => {
    if (resolvedNav.deferred) return undefined

    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => setContainersReady(true), { timeout: 3000 })
      return () => window.cancelIdleCallback(id)
    }
    const id = setTimeout(() => setContainersReady(true), 1500)
    return () => clearTimeout(id)
  }, [])

  const set = useCallback(
    ({ container, panel, tab }: { container?: NavContainerConfig; panel?: NavPanelConfig; tab?: NavTabConfig }) => {
      if (container) {
        setFirstOpen(true)
        setContainersReady(true)
      }
      setCurrent((prev: NavSelection) => {
        if (tab) {
          prev.tabId = tab.id
        } else if (panel) {
          prev.panelId = panel.id
          if (panel.tabs?.length) {
            prev.tabId = panel.tabs[0].id
          } else {
            prev.tabId = undefined
          }
        } else if (container) {
          prev.containerId = container.id
          if (container.panels?.length) {
            prev.panelId = container.panels[0].id
            if (container.panels[0].tabs?.length) {
              prev.tabId = container.panels[0].tabs[0].id
            } else {
              prev.tabId = undefined
            }
          } else {
            prev.tabId = undefined
            prev.panelId = undefined
          }
        } else {
          prev = {}
        }
        return { ...prev }
      })
    },
    []
  )

  const clear = useCallback(() => setCurrent({}), [])

  const close = useCallback(() => {
    setCurrent({})
    setMobileMenuVisible(false)
  }, [])

  // Activity preserves route state but cleans up layout effects when hiding it.
  // A transient menu must be closed before that page becomes visible again.
  useLayoutEffect(() => close, [close])

  // Close menu on route change
  useEffect(() => {
    close()
  }, [pathname, close])

  const isMobileOverlayOpen = isMobile && (isMobileMenuVisible || !!current.containerId)
  const isNavActive = isMobileOverlayOpen || !!current.containerId
  useScrollAdjust({
    pageType,
    isDarkNav,
    isMobileOverlayOpen,
    isNavActive,
    navRef,
    setMenuVisible,
    setMenuTextWhite,
  })

  // The route theme is available during render, so dark-route contrast updates
  // in the same frame as a soft navigation. Scroll-driven white text remains
  // specific to the transparent homepage nav.
  const isMenuTextWhite = isDarkNav || (pageType === 'home' && isHomepageMenuTextWhite)

  useClickaway({ current, clear, enabled: !isMobile })

  // Used for eager loading images on request idle
  const isFirstView = useCallback(
    (family: NavFamily) => {
      const { container, panel, tab } = family
      const first = [
        resolvedNav.containers[0].id,
        resolvedNav.containers[0].panels?.[0]?.id ?? 0,
        resolvedNav.containers[0]?.panels?.[0].tabs?.[0]?.id ?? 0,
      ]
      const check = [container?.id ?? 0, panel?.id ?? 0, tab?.id ?? 0]
      return first[0] === check[0] && first[1] === check[1] && first[2] === check[2]
    },
    [resolvedNav]
  )

  const value = useMemo(
    () => ({
      pageType,
      nav: resolvedNav,
      current,
      set,
      clear,
      isMobile,
      isMenuVisible,
      isMobileMenuVisible,
      isDarkNav,
      isMenuTextWhite,
      isFirstView,
      setMobileMenuVisible,
      registerNavRef,
      close,
      debugNav,
      containersReady,
      isContentLoading,
      navLoadFailed,
      retryNav,
    }),
    [
      pageType,
      resolvedNav,
      current,
      set,
      clear,
      isMobile,
      isMenuTextWhite,
      isMobileMenuVisible,
      isDarkNav,
      setMobileMenuVisible,
      isMenuVisible,
      isFirstView,
      setMenuVisible,
      registerNavRef,
      close,
      debugNav,
      containersReady,
      isContentLoading,
      navLoadFailed,
      retryNav,
    ]
  )

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>
}

export const useNavContext = () => {
  const context = useContext(NavContext)
  return context
}
